import { EXTERNALIMAGEFRAMES, EXTERNALIMAGEPREFIX, EXTERNALVIDEOPREFIX } from '@constants'
import { ProductDetailsImages } from '@modules/common/models'
import { defaultProductCardImage, BigPlaceholderImage } from '@resources/images'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import memoize from 'memoize-one'
import { config } from '@config'
import { storage } from '@services/storageService'
import axios from 'axios'

import { getInterfaceImageURLFromKey } from './commonServices'
import { HttpService } from './HttpService'

const getOptionsForBuyingSessionProductSizeAttributes = productAttributes => {
  const sizesObject = find(productAttributes, att => att.definition.name === 'sizes')
  return sizesObject.strVal.split(',')
}

/**
 * addOrRemoveClientExclusion
 * @param buyingSessionProductId
 * @param payload
 * @returns
 */
const addOrRemoveClientExclusion = async (
  buyingSessionProductId: string,
  payload: any
) => {
  const httpService = new HttpService()

  const url = `${config.buyingBoardServiceRestEndpoint}exclusion/client/buyingSessionProduct/${buyingSessionProductId}`
  const accessToken: string = storage.getItem('access_token')
  const response: any = await httpService.put(url, payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })

  return response
}

/**
 * It extracts interface required images
 * to mount on product details page
 * @param interfaceImages
 */
const extractInterfaceImages = (
  interfaceImages: Array<{ Name: string; Value: string }>
): ProductDetailsImages => {
  const images = []
  for (let i = 0; i < EXTERNALIMAGEFRAMES; i++) {
    const src = getInterfaceImageURLFromKey(
      interfaceImages,
      `${EXTERNALIMAGEPREFIX}_${i}`
    )
    if (src) {
      images.push({
        id: i,
        src
      })
    }
  }
  const iframeURL = getInterfaceImageURLFromKey(
    interfaceImages,
    `${EXTERNALIMAGEPREFIX}_360`
  )

  const videoURL = getInterfaceImageURLFromKey(interfaceImages, `${EXTERNALVIDEOPREFIX}`)
  return { images, iframeURL, videoURL }
}

/**
 * It prefers interfaces (like orb) images first, if no interface image found
 * it returns single image if present
 * @param interfaceImages
 * @param singleImageURL
 */
const getProductImagesForDetailsView = memoize(
  (interfaceImages, singleImageURL, isSAView = false): ProductDetailsImages => {
    if (!isEmpty(interfaceImages)) {
      return extractInterfaceImages(interfaceImages)
    } else {
      return {
        images: [
          {
            id: 0,
            src: singleImageURL
              ? singleImageURL
              : isSAView
              ? BigPlaceholderImage
              : defaultProductCardImage
          }
        ]
      }
    }
  }
)

interface DeleteProductForecast {
  buyingSessionProductId: string
  clientId: string
}

const deleteProductForecast = async (
  buyingSessionGroupId: string,
  toBeDeletedForecasts: DeleteProductForecast[]
) => {
  const url = `${config.buyingBoardServiceRestEndpoint}forecast/buyingSessionGroupId/${buyingSessionGroupId}/deleteRecords`
  const accessToken: string = storage.getItem('access_token')
  const response: any = await axios.post(url, toBeDeletedForecasts, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })

  return response
}
/* delete forecast data for client exclusions added */
const deleteForecastFromOrderMgmtSvc = async (
  buyingSessionGroupId: string,
  buyingSessionProductId: string,
  clientExclusions: string[]
) => {
  let toBeDeletedForecasts = [] as DeleteProductForecast[]
  if (clientExclusions?.length > 0) {
    clientExclusions.forEach(clientId => {
      let forecast = {} as DeleteProductForecast
      forecast.buyingSessionProductId = buyingSessionProductId
      forecast.clientId = clientId
      toBeDeletedForecasts.push(forecast)
    })

    if (toBeDeletedForecasts.length > 0) {
      deleteProductForecast(buyingSessionGroupId, toBeDeletedForecasts)
    }
  }
}

export {
  getOptionsForBuyingSessionProductSizeAttributes,
  getProductImagesForDetailsView,
  deleteForecastFromOrderMgmtSvc,
  addOrRemoveClientExclusion
}
